import { useCallback, useRef, useState } from 'react';
import type { ReactNode } from 'react';
import ConfirmationModal from '@/components/ConfirmationModal';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';

export interface ConfirmOptions {
  title: string;
  message: string;
  confirmText?: string;
  cancelText?: string;
  isDanger?: boolean;
}

/**
 * Promise-based replacement for `window.confirm`, rendered with the project's
 * styled ConfirmationModal instead of the browser's native dialog.
 *
 * Usage:
 *   const { confirm, confirmDialog } = useConfirm();
 *   ...
 *   if (!(await confirm({ title: 'Delete step', message: '…', isDanger: true }))) return;
 *   ...
 *   return <>{...page...}{confirmDialog}</>;
 *
 * Render `confirmDialog` once anywhere in the component's tree. Only one
 * confirmation can be open at a time per hook instance; a second call while
 * one is open settles the first as cancelled.
 */
export function useConfirm(): {
  confirm: (options: ConfirmOptions) => Promise<boolean>;
  confirmDialog: ReactNode;
} {
  const [options, setOptions] = useState<ConfirmOptions | null>(null);
  const resolverRef = useRef<((ok: boolean) => void) | null>(null);

  const confirm = useCallback(
    (opts: ConfirmOptions) =>
      new Promise<boolean>((resolve) => {
        resolverRef.current?.(false);
        resolverRef.current = resolve;
        setOptions(opts);
      }),
    [],
  );

  const settle = (ok: boolean) => {
    resolverRef.current?.(ok);
    resolverRef.current = null;
    setOptions(null);
  };

  const confirmDialog = options ? (
    <ConfirmationModal
      isActive
      title={options.title}
      message={options.message}
      confirmText={options.confirmText}
      cancelText={options.cancelText}
      isDanger={options.isDanger}
      onConfirm={() => { settle(true); }}
      onCancel={() => { settle(false); }}
    />
  ) : null;

  return { confirm, confirmDialog };
}

export interface PromptOptions {
  title: string;
  message?: string;
  placeholder?: string;
  confirmText?: string;
  cancelText?: string;
}

/**
 * Promise-based replacement for `window.prompt`, rendered as a styled dialog
 * with a single text input. Resolves to the entered string (possibly empty)
 * on confirm, or `null` on cancel — same contract as `window.prompt`.
 */
export function usePrompt(): {
  prompt: (options: PromptOptions) => Promise<string | null>;
  promptDialog: ReactNode;
} {
  const [options, setOptions] = useState<PromptOptions | null>(null);
  const [value, setValue] = useState('');
  const resolverRef = useRef<((v: string | null) => void) | null>(null);

  const prompt = useCallback(
    (opts: PromptOptions) =>
      new Promise<string | null>((resolve) => {
        resolverRef.current?.(null);
        resolverRef.current = resolve;
        setValue('');
        setOptions(opts);
      }),
    [],
  );

  const settle = (v: string | null) => {
    resolverRef.current?.(v);
    resolverRef.current = null;
    setOptions(null);
  };

  const promptDialog = options ? (
    <Dialog open onOpenChange={(open) => { if (!open) settle(null); }}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>{options.title}</DialogTitle>
          {options.message ? <DialogDescription>{options.message}</DialogDescription> : null}
        </DialogHeader>
        <form
          onSubmit={(e) => {
            e.preventDefault();
            settle(value);
          }}
        >
          <Input
            autoFocus
            value={value}
            onChange={(e) => { setValue(e.target.value); }}
            placeholder={options.placeholder}
          />
          <DialogFooter className="mt-4">
            <Button type="button" variant="outline" onClick={() => { settle(null); }}>
              {options.cancelText ?? 'Cancel'}
            </Button>
            <Button type="submit">{options.confirmText ?? 'OK'}</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  ) : null;

  return { prompt, promptDialog };
}
